import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, ExternalLink, Maximize2, Minus, Plus, RotateCw, X } from "lucide-react";

/**
 * Tam ekran görsel görüntüleyici (yakınlaştır / döndür / kaydır).
 *
 * Rapor fotoğrafları telefonla çekilmiş, bazen yan duran ve küçük yazılı
 * belgeler; kart içindeki küçük önizlemeden okunmuyordu. Eskiden tıklayınca
 * `window.open` ile ham dosya YENİ SEKMEDE açılıyordu: uygulamadan çıkıyor,
 * tarayıcının kendi görüntüleyicisine düşüyordunuz. Artık uygulama içinde,
 * onay ekranından ayrılmadan incelenebiliyor.
 *
 * `createPortal` ile doğrudan <body> altına basılır. Bu ŞART: Radix diyalogları
 * içeriği `transform` ile ortalar ve transform'lu bir ata, `position: fixed`
 * çocuğu viewport yerine KENDİNE göre konumlandırır — portal olmadan kaplama
 * diyaloğun içine hapsolurdu.
 */

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.25;

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

export default function ImageLightbox({ src, alt = "Görsel", filename, open, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const reset = useCallback(() => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Her açılışta sıfırdan başla: önceki görselin yakınlaştırması yeni görsele
  // taşınırsa kullanıcı boş/kayık bir alana bakıyor sanıyor.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "+" || e.key === "=") setZoom((z) => clampZoom(z + ZOOM_STEP));
      else if (e.key === "-" || e.key === "_") setZoom((z) => clampZoom(z - ZOOM_STEP));
      else if (e.key === "0") reset();
    };
    document.addEventListener("keydown", onKey);
    // Arkadaki sayfa kaydırmasın.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, reset]);

  if (!open || !src) return null;

  const startDrag = (e) => {
    dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onDrag = (e) => {
    if (!dragRef.current) return;
    setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  const onWheel = (e) => {
    setZoom((z) => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
  };

  const stop = (e) => e.stopPropagation();

  const toolbarBtn =
    "flex h-9 w-9 items-center justify-center rounded-md text-white/90 hover:bg-white/15 hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
    >
      {/* Araç çubuğu */}
      <div
        className="flex items-center justify-between gap-2 border-b border-white/10 bg-black/60 px-3 py-2"
        onClick={stop}
      >
        <p className="truncate text-sm text-white/80" title={filename || alt}>
          {filename || alt}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className={toolbarBtn}
            onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
            disabled={zoom <= MIN_ZOOM}
            title="Uzaklaştır (−)"
            aria-label="Uzaklaştır"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-14 text-center text-xs tabular-nums text-white/70">
            %{Math.round(zoom * 100)}
          </span>
          <button
            type="button"
            className={toolbarBtn}
            onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
            disabled={zoom >= MAX_ZOOM}
            title="Yakınlaştır (+)"
            aria-label="Yakınlaştır"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={toolbarBtn}
            onClick={() => setRotation((r) => (r + 90) % 360)}
            title="90° döndür"
            aria-label="Döndür"
          >
            <RotateCw className="h-4 w-4" />
          </button>
          <button type="button" className={toolbarBtn} onClick={reset} title="Sığdır (0)" aria-label="Sığdır">
            <Maximize2 className="h-4 w-4" />
          </button>
          <a
            href={src}
            download={filename || "rapor.jpg"}
            className={toolbarBtn}
            title="İndir"
            aria-label="İndir"
          >
            <Download className="h-4 w-4" />
          </a>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className={toolbarBtn}
            title="Yeni sekmede aç"
            aria-label="Yeni sekmede aç"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button type="button" className={toolbarBtn} onClick={onClose} title="Kapat (Esc)" aria-label="Kapat">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Görsel alanı — boşluğa tıklayınca kapanır */}
      <div
        className="flex flex-1 items-center justify-center overflow-hidden"
        onWheel={onWheel}
        onMouseMove={onDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          onClick={stop}
          onMouseDown={startDrag}
          onDoubleClick={() => setZoom((z) => (z >= 2 ? 1 : 2))}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            cursor: zoom > 1 ? "grab" : "zoom-in",
          }}
          className="max-h-[calc(100vh-7rem)] max-w-[95vw] select-none object-contain transition-transform duration-100"
        />
      </div>

      <p className="pb-2 text-center text-[11px] text-white/45" onClick={stop}>
        Tekerlek veya +/− ile yakınlaştır · sürükleyerek kaydır · çift tıkla büyüt · Esc ile kapat
      </p>
    </div>,
    document.body
  );
}
